import { PDFDocument } from "pdf-lib";
import * as pdfjsLib from "pdfjs-dist";
import { genId } from "./helpers";

pdfjsLib.GlobalWorkerOptions.workerSrc = "https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js";

function mapAcroType(className) {
    return (
        {
            PDFTextField: "text",
            PDFCheckBox: "checkbox",
            PDFDropdown: "dropdown",
            PDFOptionList: "dropdown",
            PDFRadioGroup: "checkbox",
            PDFSignature: "signature",
        }[className] || "text"
    );
}

function fontSizeNearField(textItems, field) {
    if (!textItems || textItems.length === 0) return 12;

    let best = textItems[0];
    let bestDist = Infinity;

    for (const item of textItems) {
        const dy = Math.abs(item.yPct - (field.y + field.h / 2));
        const dx = Math.abs(item.xPct - field.x);
        const dist = dy * 2 + dx;
        if (dist < bestDist) {
            bestDist = dist;
            best = item;
        }
    }

    return best.fontSize;
}

// Returns Map<pageIndex, [{xPct, yPct, fontSize}]>
export async function extractFieldFontSizes(arrayBuffer) {
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer.slice(0) }).promise;
    const result = new Map();

    for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const vp = page.getViewport({ scale: 1 });
        const content = await page.getTextContent();

        const items = content.items
            .filter((item) => item.transform && item.str.trim().length > 0)
            .map((item) => ({
                yPct: ((vp.height - item.transform[5]) / vp.height) * 100,
                xPct: (item.transform[4] / vp.width) * 100,
                fontSize: Math.abs(item.transform[0]),
            }));

        result.set(i - 1, items);
    }

    return result;
}

// Returns detected fields array
export async function autoDetectFields(arrayBuffer, defaultRoleId, textItemsByPage) {
    let pdfDoc;

    try {
        pdfDoc = await PDFDocument.load(arrayBuffer.slice(0), {
            ignoreEncryption: true,
        });
    } catch (err) {
        console.warn("pdf-lib could not load PDF for field detection:", err);
        return [];
    }

    let form;
    try {
        form = pdfDoc.getForm();
    } catch {
        return [];
    }

    const acroFields = form.getFields();
    if (!acroFields.length) return [];

    const pdfPages = pdfDoc.getPages();
    const detected = [];

    for (const field of acroFields) {
        let widgets = [];
        try {
            widgets = field.acroField.getWidgets();
        } catch {
            continue;
        }

        for (const widget of widgets) {
            let rect;
            try {
                rect = widget.getRectangle();
            } catch {
                continue;
            }

            // Find which page this widget belongs to
            const pageRef = widget.P();
            const pageIndex = pageRef
                ? pdfPages.findIndex((p) => p.ref === pageRef)
                : 0;
            const safePage = Math.max(0, pageIndex);
            const page = pdfPages[safePage];

            if (!page) continue;

            const { width: pw, height: ph } = page.getSize();

            // Convert PDF bottom-left → top-left percentage
            const xPct = (rect.x / pw) * 100;
            const yPct = ((ph - rect.y - rect.height) / ph) * 100;
            const wPct = (rect.width / pw) * 100;
            const hPct = (rect.height / ph) * 100;

            const textItems = textItemsByPage?.get(safePage) || [];
            const nearestFontSize = fontSizeNearField(textItems, {
                x: xPct, y: yPct, w: wPct, h: hPct,
            });

            detected.push({
                id: genId(),
                type: mapAcroType(field.constructor.name),
                roleId: defaultRoleId,
                label: field.getName() || "Field",
                required: false,
                placeholder: "",
                x: Math.max(0, xPct),
                y: Math.max(0, yPct),
                w: Math.max(1, wPct),
                h: Math.max(0.5, hPct),
                pageIndex: safePage,
                fontSize: Math.round(nearestFontSize),
            });
        }
    }

    return detected;
}